// src/lib/dateUtils.js
// Centralized date utilities with Poland timezone (Europe/Warsaw) as default
// This prevents timezone bugs when converting dates to/from strings
//
// Time of day: always use 24-hour format (no AM/PM). Poland uses 24h exclusively.
// Use HH:MM or HH:MM:SS for storage and display (e.g. "14:30", "09:00").

const POLAND_TIMEZONE = 'Europe/Warsaw';

/**
 * Format a date as YYYY-MM-DD in Poland timezone
 * Use this instead of date.toISOString().split('T')[0] which converts to UTC
 * @param {Date|string} date - Date object or date string
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatDatePL = (date) => {
  const d = new Date(date);
  // Use Intl.DateTimeFormat with Poland timezone for accurate local date
  const formatter = new Intl.DateTimeFormat('sv-SE', {
    timeZone: POLAND_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  return formatter.format(d);
};

/**
 * Format a date as YYYY-MM-DD using local system timezone
 * Simpler version that doesn't require Intl API
 * @param {Date|string} date - Date object or date string
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const formatLocalDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a YYYY-MM-DD date string as a local date (not UTC)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Date object in local timezone
 */
export const parseLocalDate = (dateString) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Get current date in Poland timezone as YYYY-MM-DD string
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayPL = () => {
  return formatDatePL(new Date());
};

/**
 * Get current date as Date object set to midnight local time
 * @returns {Date} Today's date at midnight
 */
export const getTodayLocal = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Get start of week (Monday) for a given date in local timezone
 * @param {Date|string} date - Date object or date string
 * @returns {Date} Monday of the week containing the given date
 */
export const getWeekStartLocal = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  // Adjust for Monday start (0 = Sunday, so Monday = 1)
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.getFullYear(), d.getMonth(), diff);
};

/**
 * Get an array of weekday dates (Mon-Fri) starting from a given date
 * @param {Date|string} weekStart - Start date (should be Monday)
 * @returns {Date[]} Array of 5 Date objects (Mon-Fri)
 */
export const getWeekDaysLocal = (weekStart) => {
  const start = new Date(weekStart);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
};

/**
 * Get an array of weekday date strings (Mon-Fri) in YYYY-MM-DD format
 * @param {Date|string} weekStart - Start date (should be Monday)
 * @returns {string[]} Array of 5 date strings (Mon-Fri)
 */
export const getWeekDatesLocal = (weekStart) => {
  return getWeekDaysLocal(weekStart).map(formatLocalDate);
};

/**
 * Add days to a date in local timezone
 * @param {Date|string} date - Date object or date string
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date} New date
 */
export const addDaysLocal = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

/**
 * Check if two dates are the same day (ignoring time)
 * @param {Date|string} date1 - First date
 * @param {Date|string} date2 - Second date
 * @returns {boolean} True if same day
 */
export const isSameDayLocal = (date1, date2) => {
  return formatLocalDate(date1) === formatLocalDate(date2);
};

/**
 * Get the day of week name in Polish
 * @param {Date|string} date - Date object or date string
 * @returns {string} Day name in Polish (e.g., "poniedziałek")
 */
export const getDayNamePL = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('pl-PL', { weekday: 'long' });
};

/**
 * Get the day of week key for template storage
 * @param {Date|string} date - Date object or date string
 * @returns {string} Day key (monday, tuesday, etc.)
 */
export const getDayKey = (date) => {
  const d = new Date(date);
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return days[d.getDay()];
};

/**
 * Format a date for display in Polish locale
 * @param {Date|string} date - Date object or date string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDisplayPL = (date, options = {}) => {
  const d = new Date(date);
  const defaultOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options
  };
  return d.toLocaleDateString('pl-PL', defaultOptions);
};

/**
 * Get month name in Polish
 * @param {Date|string} date - Date object or date string
 * @returns {string} Month name in Polish
 */
export const getMonthNamePL = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('pl-PL', { month: 'long' });
};

/**
 * Check if a date is a weekend (Saturday or Sunday)
 * @param {Date|string} date - Date object or date string
 * @returns {boolean} True if weekend
 */
export const isWeekend = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  return day === 0 || day === 6;
};

/**
 * Check if a date is a weekday (Monday-Friday)
 * @param {Date|string} date - Date object or date string
 * @returns {boolean} True if weekday
 */
export const isWeekday = (date) => {
  return !isWeekend(date);
};

/**
 * Get date range as array of YYYY-MM-DD strings (inclusive)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {string[]} Array of date strings
 */
export const getDateRangeLocal = (startDate, endDate) => {
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);

  while (current <= end) {
    dates.push(formatLocalDate(current));
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

/**
 * Get weekday dates in a range (excluding weekends)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {string[]} Array of weekday date strings
 */
export const getWeekdaysInRangeLocal = (startDate, endDate) => {
  return getDateRangeLocal(startDate, endDate).filter(dateStr => {
    const date = parseLocalDate(dateStr);
    return isWeekday(date);
  });
};

// Export timezone constant for reference
export const TIMEZONE = POLAND_TIMEZONE;
